import { Col, Row } from "react-bootstrap";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import BasicModal from "@/Modals/BasicModal";
import LocalizedInput from "@/components/LocalizedInput";
import RenderToast from "@/components/RenderToast";
import { VideoCategoryInitialValues } from "@/data/ContentManagement/VideoCategories/initial-values";
import VideoCategorySchema from "@/schema/ContentManagement/VideoCategories/VideoCategorySchema";
import StatusInput from "@/components/StatusInput";
import { videoCategoriesApi } from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { useSelector } from "react-redux";

const VideoCategoryModal = forwardRef((_, ref) => {
  const locales = useSelector(
    (state) => state?.siteConfigurationReducer?.locales
  );

  const localeCodes = useMemo(() => {
    return locales ? locales.map((locale) => locale.code) : [];
  }, [locales]);

  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${videoCategoriesApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Video Category ${action} successfully.`,
    });
  };
  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${videoCategoriesApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: videoCategoriesApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: VideoCategoryInitialValues(
      localeCodes,
      modalState.id ? showData : null
    ),
    validationSchema: VideoCategorySchema(localeCodes, !!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) formData.append("_method", "PUT");
    formData.append("code", values.code);
    formData.append("status", values.status);
    Object.keys(values.localizations).forEach((key) => {
      if (values.localizations[key]) {
        formData.append(`localizations[${key}]`, values.localizations[key]);
      }
    });
    if (values.icon) formData.append("icon", values.icon);

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Video Category" : "Add Video Category"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <LocalizedInput
        formik={formik}
        error={saveError}
        isEdit={!!modalState.id}
      />
      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default VideoCategoryModal;
